import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RenderAccountService } from '../render-account.service';
import { RenderFlowService } from '../render-flow.service';
import { ensureRenderFontsLoaded } from '../render-fonts';
import { RenderHelpService } from '../render-help.service';
import { RENDER_LANDING_URL } from '../render-home';
import { RENDER_LEGAL_DOCS } from '../legal/render-legal.data';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { easeWindowScrollTo, holdPageScrollbar } from '../render-scroll';
import { RenderUploadFile } from '../render-upload.inspector';
import {
  RENDER_CHANNEL_SUBSCRIBE_URL,
  RENDER_CHANNEL_SUBSCRIBERS,
  RENDER_CHANNEL_URL,
  RENDER_EPISODES,
  RENDER_INSTAGRAM_URL,
  RENDER_REEL_PORTRAIT_RATIO,
  RENDER_V2_BANDS,
  RENDER_V2_HERO,
  RENDER_V2_NOTES,
  RENDER_V2_STEPS,
  RenderBand,
  RenderEpisode,
  renderEpisodeFrame,
  RenderFullFile,
  RenderReel,
  renderEpisodeUrl,
} from '../render-v2.data';
import { RENDER_OUTPUT_SPEC, RENDER_PRICE_USD, RenderSample, renderDimensions } from '../render.data';

type Format = 'landscape' | 'portrait';

// The renders are HEVC; a browser that cannot play it gets the H.264 copy. Chrome answers "" to a
// bare "hvc1", so the full codec string of our files is asked for.
function canPlayHevc(): boolean {
  try {
    return !!document.createElement('video').canPlayType('video/mp4; codecs="hvc1.1.6.L123.B0"');
  } catch {
    return false;
  }
}

// A video gets its source the first time it is played or primed, and again once its film has
// changed (a band on a phone turned to its portrait). False when it has none.
function prime(video: HTMLVideoElement | undefined, hevc: boolean): boolean {
  const source = video && ((hevc && video.dataset['hevc']) || video.dataset['h264']);
  if (!video || !source) {
    return false;
  }
  if (video.getAttribute('src') !== source) {
    video.muted = true;
    video.preload = 'auto';
    video.src = source;
  }
  return true;
}

// iOS loads only metadata until play() is called, so play() is called right away, never after
// waiting for canplay.
function play(video: HTMLVideoElement | undefined, hevc: boolean): void {
  if (prime(video, hevc)) {
    video?.play().catch(() => undefined);
  }
}

function pause(video: HTMLVideoElement | undefined): void {
  video?.pause();
}

// "8.5 MB": decimal megabytes, one place.
function megabytes(bytes: number): string {
  return `${(bytes / 1e6).toFixed(1)} MB`;
}

// Each render across the full window, the room dimming while it fills the screen: the page goes
// dark once a band is 55% on screen and comes back when none is 30% on screen.
const DIM_FROM = 0.55;
const LIGHT_BELOW = 0.3;
// The page's phone width ($v2-phone): the bands play their portrait films there, as a feed.
const PHONE_QUERY = '(max-width: 760px)';
// The feed takes the swipes while it covers the window, give or take this much.
const FEED_SLACK_PX = 4;
// The hero swaps its format every 5 s while on screen, until the visitor picks one.
const MORPH_EVERY_MS = 5000;
// The portrait video drifts from the landscape one; it is pulled back past 80 ms.
const SYNC_EVERY_MS = 1500;
const SYNC_TOLERANCE_S = 0.08;

// The render landing, /render (docs/render-v2-landing.md): the static concept House Lights the boss
// saw at /render-demo (src/render-demo, archived at /render-demo-archive) on the real flow: the
// profile menu, sign-in on the page, the whole-window drop for a signed-in user, and a sample's
// played upload from each render. It stood at /render-demo beside the first landing until the owner
// made it the only one (2026-10-16); the flow wears its cream and ink. Unencapsulated: everything
// sits under .render-v2.
@Component({
  selector: 'app-render-landing-v2',
  templateUrl: './render-landing-v2.component.html',
  styleUrls: ['./render-landing-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RenderLandingV2Component implements OnInit, AfterViewInit, OnDestroy {
  public readonly homeUrl = RENDER_LANDING_URL;
  public readonly legalDocs = RENDER_LEGAL_DOCS;
  public readonly hero = RENDER_V2_HERO;
  public readonly bands = RENDER_V2_BANDS;
  public readonly steps = RENDER_V2_STEPS;
  public readonly notes = RENDER_V2_NOTES;
  public readonly price = RENDER_PRICE_USD;
  public readonly landscapeSize = renderDimensions('landscape');
  public readonly portraitSize = renderDimensions('portrait');
  public readonly motion = `360°, ${RENDER_OUTPUT_SPEC.seconds} s, ${RENDER_OUTPUT_SPEC.fps} fps`;
  public readonly priceFine = `FBX & USDZ · ${RENDER_OUTPUT_SPEC.fps} fps · ${RENDER_OUTPUT_SPEC.container} · ${RENDER_OUTPUT_SPEC.seconds} sec`;

  public readonly channelUrl = RENDER_CHANNEL_URL;
  public readonly instagramUrl = RENDER_INSTAGRAM_URL;
  public readonly subscribeUrl = RENDER_CHANNEL_SUBSCRIBE_URL;
  public readonly subscribers = RENDER_CHANNEL_SUBSCRIBERS;
  public readonly episodes = RENDER_EPISODES;

  // No "Specification": it sits right under "How it works", and the two scrolled to about the same
  // place (designer, 2026-10-12). The section stays on the page.
  public readonly anchors: ReadonlyArray<{ id: string; label: string }> = [
    { id: 'examples', label: 'Examples' },
    { id: 'how-it-works', label: 'How it works' },
    { id: 'pricing', label: 'Pricing' },
  ];

  public format: Format = 'landscape';
  // A phone gets each render's portrait film (designer, 2026-10-13: "эти модели должны стать
  // вертикальными").
  private readonly phone = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_QUERY) : null;
  public portraitBands = !!this.phone?.matches;
  // The frame size under the hero, a character at a time so it rolls in (fresh objects each time,
  // so every character is a new element and animates again).
  public sizeCharacters: ReadonlyArray<{ character: string }> = [];
  // "Full quality": a band's render as a customer gets it, over the page (renderFullQuality).
  public viewer: { band: RenderBand; format: Format } | null = null;

  @ViewChild('root', { static: true }) private root?: ElementRef<HTMLElement>;
  @ViewChild('morph') private morph?: ElementRef<HTMLElement>;
  @ViewChild('morphLandscape') private morphLandscape?: ElementRef<HTMLVideoElement>;
  @ViewChild('morphPortrait') private morphPortrait?: ElementRef<HTMLVideoElement>;
  @ViewChild('rail') private rail?: ElementRef<HTMLElement>;
  @ViewChildren('band') private bandElements?: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('feed') private feed?: ElementRef<HTMLElement>;
  @ViewChild('viewerVideo') private viewerVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('viewerClose') private viewerClose?: ElementRef<HTMLButtonElement>;

  private readonly hevc = canPlayHevc();
  private readonly reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // The hero swaps formats on its own until the visitor picks one (never under reduced motion).
  private autoMorph = !this.reduceMotion;
  private morphInView = false;
  private readonly observers: IntersectionObserver[] = [];
  private readonly timers: number[] = [];
  private releasePageScrollbar?: () => void;
  private queryParamsSubscription?: Subscription;
  private railFrame = 0;
  private feedFrame = 0;
  // While the viewer is open: the page's films it paused, the button that opened it, and the second
  // the other format picks up on.
  private pausedForViewer: HTMLVideoElement[] = [];
  private viewerOpener: HTMLElement | null = null;
  private viewerResumeAt = 0;
  private readonly onResize = (): void => {
    this.placeMorphFrame();
    this.placeFeed();
  };
  private readonly onScroll = (): void => {
    if (!this.feedFrame) {
      this.feedFrame = requestAnimationFrame(() => {
        this.feedFrame = 0;
        this.placeFeed();
      });
    }
  };
  // Turned across the phone width: a playing band goes on with its other film, the rest swap when
  // they next play; the bands become a feed or stop being one.
  private readonly onPhoneChange = (): void => {
    this.zone.run(() => (this.portraitBands = !!this.phone?.matches));
    this.bandElements?.forEach((band) => {
      const video = band.nativeElement.querySelector('video') ?? undefined;
      if (video && !video.paused) {
        play(video, this.hevc);
      }
    });
    this.markFeed();
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly account: RenderAccountService,
    private readonly flow: RenderFlowService,
    private readonly help: RenderHelpService,
    private readonly zone: NgZone,
    // "Cookie settings" in the footer: production only, with the banner it reopens.
    public readonly analytics: AnalyticsService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    ensureRenderFontsLoaded(this.document);
    this.releasePageScrollbar = holdPageScrollbar(this.document);
    // A guest who opened a step of the flow was sent here with ?next=… (RenderSignedInGuard): the
    // sign-in opens over the page, and the flow goes on once they are in; closed, the page stays.
    this.queryParamsSubscription = this.route.queryParamMap.subscribe((params) => {
      const next = params.get('next');
      if (!next) {
        return;
      }
      this.router.navigate([], { relativeTo: this.route, queryParams: { next: null }, queryParamsHandling: 'merge', replaceUrl: true });
      this.account.whenSignedIn(() => this.router.navigateByUrl(next));
    });
    this.rollSize();
    this.markFeed();
  }

  ngAfterViewInit(): void {
    // The portrait film's shape, for its place in the hero's frame (.mp).
    this.morph?.nativeElement.style.setProperty('--portrait-ratio', String(RENDER_REEL_PORTRAIT_RATIO));
    this.zone.runOutsideAngular(() => {
      this.watchMorph();
      this.watchBands();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.phone?.addEventListener?.('change', this.onPhoneChange);
      this.placeFeed();
    });
  }

  ngOnDestroy(): void {
    this.observers.forEach((observer) => observer.disconnect());
    this.timers.forEach((timer) => window.clearInterval(timer));
    cancelAnimationFrame(this.railFrame);
    cancelAnimationFrame(this.feedFrame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    this.phone?.removeEventListener?.('change', this.onPhoneChange);
    this.document.documentElement.classList.remove('render-v2-feed');
    this.document.documentElement.classList.remove('render-v2-viewing');
    this.releasePageScrollbar?.();
    this.queryParamsSubscription?.unsubscribe();
  }

  public get isSignedIn(): boolean {
    return this.account.isSignedIn;
  }

  public trackByBand(index: number, band: RenderBand): string {
    return band.sample.id;
  }

  public bandReel(band: RenderBand): RenderReel {
    return this.portraitBands ? band.portrait : band.reel;
  }

  public trackByEpisode(index: number, episode: RenderEpisode): string {
    return episode.id;
  }

  public episodeUrl(episode: RenderEpisode): string {
    return renderEpisodeUrl(episode);
  }

  public episodeFrame(episode: RenderEpisode): string {
    return renderEpisodeFrame(episode);
  }

  // "#11" for the newest of eleven: "#", as they write it in the US (designer, 2026-11-04).
  public episodeNumber(index: number): string {
    return `#${String(this.episodes.length - index).padStart(2, '0')}`;
  }

  // --- CTAs ---

  // Guests sign in on this page (the popup closes itself after a sign-in), then continue.
  public onStartClick(event?: Event): void {
    event?.preventDefault();
    this.account.whenSignedIn(() => this.router.navigateByUrl('/render/upload'));
  }

  // "Try with this model": the upload step plays the sample's upload, as if it had been dropped
  // into its zone, and the flow goes on to the sample's preview.
  public onTryClick(sample: RenderSample): void {
    this.account.whenSignedIn(() => {
      this.flow.setPendingSample(sample);
      this.router.navigateByUrl('/render/upload');
    });
  }

  // A drop anywhere on the page (signed-in users only), handed to the upload step as on /render.
  public onFilesDropped(files: RenderUploadFile[]): void {
    this.flow.setPendingFiles(files);
    this.router.navigateByUrl('/render/upload');
  }

  public openGuide(): void {
    this.help.open();
  }

  // --- in-page navigation ---

  public scrollTo(id: string, event?: Event): void {
    event?.preventDefault();
    const target = this.document.getElementById(id);
    if (!target) {
      return;
    }
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const examples = id === 'examples' ? this.examplesTop(target) : null;
    const top = examples ?? target.getBoundingClientRect().top - margin;
    easeWindowScrollTo(top + window.scrollY);
  }

  // "Examples" opens on the first render filling the window, the room dimmed (designer, 2026-10-13:
  // it stopped on the title with the render cut off below, by a different amount on every window
  // height). A render shorter than the window sits on its middle, the air above it no deeper than
  // the title's margin, so no cut line of the title shows; on a tall window, where the title and
  // the render fit together, both are shown, centred, the air above them no deeper than the
  // section's own, so the section before never peeks in. Relative to the window's top.
  private examplesTop(section: HTMLElement): number | null {
    // A phone's feed goes back to its first film.
    if (this.portraitBands && this.feed) {
      this.feed.nativeElement.scrollTop = 0;
    }
    const band = this.bandElements?.first?.nativeElement;
    const head = section.querySelector('.sec-head');
    if (!band || !head) {
      return null;
    }
    const view = window.innerHeight;
    const bandBox = band.getBoundingClientRect();
    const headBox = head.getBoundingClientRect();
    const air = (view - bandBox.height) / 2;
    const margin = bandBox.top - headBox.bottom;
    const both = bandBox.bottom - headBox.top;
    if (air > margin && both <= view) {
      const sectionAir = headBox.top - section.getBoundingClientRect().top;
      return headBox.top - Math.min((view - both) / 2, sectionAir);
    }
    return bandBox.top - Math.max(0, Math.min(air, margin));
  }

  public onMarkClick(event: MouseEvent): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    easeWindowScrollTo(0);
  }

  // --- the hero: one frame, two compositions ---

  public onFormatClick(format: Format): void {
    this.autoMorph = false;
    this.setFormat(format);
  }

  private setFormat(format: Format): void {
    if (format === this.format) {
      return;
    }
    const landscape = this.morphLandscape?.nativeElement;
    const portrait = this.morphPortrait?.nativeElement;
    // The other video picks up on the same second.
    try {
      if (landscape && portrait) {
        if (format === 'portrait') {
          portrait.currentTime = landscape.currentTime % (portrait.duration || RENDER_OUTPUT_SPEC.seconds);
        } else {
          landscape.currentTime = portrait.currentTime % (landscape.duration || RENDER_OUTPUT_SPEC.seconds);
        }
      }
    } catch {
      // Not loaded yet: both start from the beginning anyway.
    }
    this.format = format;
    this.placeMorphFrame();
    this.rollSize();
  }

  private rollSize(): void {
    const text = this.format === 'portrait' ? ` ${this.portraitSize}` : this.landscapeSize;
    this.sizeCharacters = Array.from(text).map((character) => ({ character }));
  }

  // The frame narrows from 16:9 to the portrait's shape by clipping its sides.
  private placeMorphFrame(): void {
    const morph = this.morph?.nativeElement;
    if (!morph) {
      return;
    }
    const side = this.format === 'portrait' ? Math.max(0, (morph.clientWidth - morph.clientHeight * RENDER_REEL_PORTRAIT_RATIO) / 2) : 0;
    morph.style.setProperty('--side', `${side}px`);
  }

  private watchMorph(): void {
    const morph = this.morph?.nativeElement;
    const landscape = this.morphLandscape?.nativeElement;
    const portrait = this.morphPortrait?.nativeElement;
    if (!morph || !landscape || !portrait || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      this.morphInView = entries[entries.length - 1].isIntersecting;
      if (this.morphInView && !this.reduceMotion && !this.viewer) {
        play(landscape, this.hevc);
        play(portrait, this.hevc);
      } else {
        pause(landscape);
        pause(portrait);
      }
    });
    observer.observe(morph);
    this.observers.push(observer);

    this.timers.push(window.setInterval(() => {
      if (this.autoMorph && this.morphInView && !this.viewer) {
        this.zone.run(() => this.setFormat(this.format === 'portrait' ? 'landscape' : 'portrait'));
      }
    }, MORPH_EVERY_MS));
    this.timers.push(window.setInterval(() => {
      if (!landscape.paused && Math.abs(portrait.currentTime - landscape.currentTime) > SYNC_TOLERANCE_S) {
        portrait.currentTime = landscape.currentTime;
      }
    }, SYNC_EVERY_MS));
  }

  // --- the bands: house lights ---

  private watchBands(): void {
    const root = this.root?.nativeElement;
    const bands = this.bandElements?.map((band) => band.nativeElement) ?? [];
    if (!root || bands.length === 0 || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const ratios = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        ratios.set(entry.target, entry.intersectionRatio);
        const video = entry.target.querySelector('video') ?? undefined;
        if (entry.isIntersecting && !this.reduceMotion && !this.viewer) {
          play(video, this.hevc);
        } else {
          pause(video);
        }
      });
      // The band most on screen, past half, is the current one: its words come up (a phone's feed)
      // and the next film loads behind it. Between two, neither is.
      let current: HTMLElement | undefined;
      bands.forEach((band) => {
        const ratio = ratios.get(band) ?? 0;
        if (ratio >= DIM_FROM && (!current || ratio > (ratios.get(current) ?? 0))) {
          current = band;
        }
      });
      if (!current?.classList.contains('is-current')) {
        bands.forEach((band) => band.classList.toggle('is-current', band === current));
        const next = current && bands[bands.indexOf(current) + 1];
        if (next && !this.reduceMotion) {
          prime(next.querySelector('video') ?? undefined, this.hevc);
        }
      }
      const ratioList = [...ratios.values()];
      const dim = root.classList.contains('is-dim');
      if (!dim && ratioList.some((ratio) => ratio >= DIM_FROM)) {
        root.classList.add('is-dim');
      } else if (dim && !ratioList.some((ratio) => ratio >= LIGHT_BELOW)) {
        root.classList.remove('is-dim');
      }
    }, { threshold: [0, LIGHT_BELOW, DIM_FROM, 0.8, 1] });
    bands.forEach((band) => observer.observe(band));
    this.observers.push(observer);
  }

  // --- "Full quality": the render's own file, over the page ---

  // "1080p · 60 fps · 8.5 MB": the short side, and megabytes as Finder and Explorer count them.
  public fullLabel(file: RenderFullFile): string {
    return `${Math.min(file.width, file.height)}p · ${RENDER_OUTPUT_SPEC.fps} fps · ${megabytes(file.bytes)}`;
  }

  public viewerFile(viewer: { band: RenderBand; format: Format }): RenderFullFile | undefined {
    return (viewer.format === 'portrait' ? viewer.band.portrait : viewer.band.reel).full;
  }

  public viewerSpec(file: RenderFullFile): string {
    return `${file.width} × ${file.height} · ${RENDER_OUTPUT_SPEC.fps} fps · ${RENDER_OUTPUT_SPEC.codec} · ${megabytes(file.bytes)}`;
  }

  // Opens on the format the band was showing; the page's films hold still underneath.
  public openViewer(band: RenderBand, event: Event): void {
    this.viewerOpener = event.currentTarget as HTMLElement | null;
    this.pausedForViewer = Array.from(this.root?.nativeElement.querySelectorAll('video') ?? []).filter((video) => !video.paused);
    this.pausedForViewer.forEach((video) => video.pause());
    this.viewerResumeAt = 0;
    this.viewer = { band, format: this.portraitBands ? 'portrait' : 'landscape' };
    this.document.documentElement.classList.add('render-v2-viewing');
    setTimeout(() => this.viewerClose?.nativeElement.focus());
  }

  // The other format picks up on the same second, as the hero's frame does.
  public setViewerFormat(format: Format): void {
    if (!this.viewer || this.viewer.format === format) {
      return;
    }
    this.viewerResumeAt = this.viewerVideo?.nativeElement.currentTime ?? 0;
    this.viewer = { ...this.viewer, format };
  }

  // Started here, muted by property: Chrome ignores the `muted` attribute Angular sets on a created
  // video, so `autoplay` alone stays paused (as prime() does for the page's films).
  public onViewerMetadata(): void {
    const video = this.viewerVideo?.nativeElement;
    if (!video) {
      return;
    }
    if (this.viewerResumeAt) {
      video.currentTime = this.viewerResumeAt % (video.duration || RENDER_OUTPUT_SPEC.seconds);
    }
    video.muted = true;
    video.play().catch(() => undefined);
  }

  @HostListener('document:keydown.escape')
  public closeViewer(): void {
    if (!this.viewer) {
      return;
    }
    this.viewer = null;
    this.document.documentElement.classList.remove('render-v2-viewing');
    this.pausedForViewer.forEach((video) => play(video, this.hevc));
    this.pausedForViewer = [];
    this.viewerOpener?.focus();
    this.viewerOpener = null;
  }

  // Tab stays on the viewer's controls while it is open.
  public onViewerTab(event: Event): void {
    const panel = event.currentTarget as HTMLElement;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button, video[controls]'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const keyboard = event as KeyboardEvent;
    if (keyboard.shiftKey && this.document.activeElement === first) {
      keyboard.preventDefault();
      last?.focus();
    } else if (!keyboard.shiftKey && this.document.activeElement === last) {
      keyboard.preventDefault();
      first?.focus();
    }
  }

  // --- a phone: the bands as a feed (the app's models feed, models-card) ---

  // The page snaps to the feed (html.render-v2-feed) while the bands are one.
  private markFeed(): void {
    this.document.documentElement.classList.toggle('render-v2-feed', this.portraitBands);
    this.placeFeed();
  }

  // The feed takes the swipes only while it covers the window; otherwise they scroll the page, so a
  // feed half on screen never holds it.
  private placeFeed(): void {
    const feed = this.feed?.nativeElement;
    if (!feed) {
      return;
    }
    const box = feed.getBoundingClientRect();
    const live = this.portraitBands && box.top <= FEED_SLACK_PX && box.bottom >= window.innerHeight - FEED_SLACK_PX;
    feed.classList.toggle('is-live', live);
  }

  // --- Community: the rail's arrows move it by two cards ---

  // Own easing, with snapping held off while it runs: src/js/smoothscroll.js takes over every
  // smooth scroll in the app (render-scroll.ts).
  public onRailClick(direction: 1 | -1): void {
    const rail = this.rail?.nativeElement;
    const card = rail?.querySelector('li');
    if (!rail || !card) {
      return;
    }
    const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || '0');
    const max = rail.scrollWidth - rail.clientWidth;
    const from = rail.scrollLeft;
    const to = Math.min(max, Math.max(0, (Math.round(from / step) + direction * 2) * step));
    cancelAnimationFrame(this.railFrame);
    if (this.reduceMotion || Math.abs(to - from) < 2) {
      rail.scrollLeft = to;
      return;
    }
    rail.style.scrollSnapType = 'none';
    const startedAt = performance.now();
    const duration = 520;
    this.zone.runOutsideAngular(() => {
      const frame = (now: number): void => {
        const t = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        rail.scrollLeft = from + (to - from) * eased;
        if (t < 1) {
          this.railFrame = requestAnimationFrame(frame);
        } else {
          rail.style.scrollSnapType = '';
        }
      };
      this.railFrame = requestAnimationFrame(frame);
    });
  }
}
